import { useEffect, useState } from "react";
import axios from "axios";
import * as Icons from "lucide-react";
import { PUBLIC_BASE } from "../lib/api";

/**
 * "247 views · 18 cities · 32 leads this week" strip shown atop the public
 * realtor dashboard.  This is a *seller-facing* social-proof tool: a
 * prospective seller landing on the page sees the realtor is moving real
 * traffic and converting it.
 */
export default function PublicEngagementStrip({ token, brand }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = () => {
      axios
        .get(`${PUBLIC_BASE}/api/share/dashboards/${token}/engagement`)
        .then((r) => {
          if (!cancelled) setStats(r.data);
        })
        .catch(() => {});
    };
    load();
    const id = setInterval(load, 60000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [token]);

  if (!stats) return null;

  const items = [
    {
      icon: "Eye",
      value: fmtCompact(stats.total_views),
      label: stats.total_views === 1 ? "view" : "views",
      visible: stats.total_views > 0,
    },
    {
      icon: "Heart",
      value: fmtCompact(stats.total_reactions || 0),
      label: (stats.total_reactions || 0) === 1 ? "reaction" : "reactions",
      visible: (stats.total_reactions || 0) > 0,
    },
    {
      icon: "MapPin",
      value: stats.cities,
      label: stats.cities === 1 ? "city" : "cities",
      visible: stats.cities > 0,
    },
    {
      icon: "Inbox",
      value: stats.leads_week,
      label: "leads this week",
      visible: stats.leads_week > 0,
    },
    {
      icon: "Film",
      value: stats.reels_total,
      label: stats.reels_total === 1 ? "reel" : "reels",
      visible: stats.reels_total > 0,
    },
  ].filter((x) => x.visible);

  if (items.length === 0) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-x-6 gap-y-3 px-4 py-3 rounded-xl border bg-white/[0.02]"
      style={{ borderColor: brand ? `${brand}30` : "rgb(255 255 255 / 0.08)" }}
      data-testid="public-engagement-strip"
    >
      {items.map((it, i) => {
        const I = Icons[it.icon];
        return (
          <div
            key={i}
            className="flex items-center gap-2 text-zinc-300"
            data-testid={`engagement-stat-${it.icon.toLowerCase()}`}
          >
            <I size={13} style={{ color: brand || "#71717a" }} />
            <span className="font-display text-lg leading-none text-white">
              {it.value}
            </span>
            <span className="text-[11px] text-zinc-500 lowercase tracking-wide">
              {it.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function fmtCompact(n) {
  n = Number(n) || 0;
  if (n < 1000) return n.toString();
  if (n < 10000) return `${(n / 1000).toFixed(1)}k`;
  if (n < 1_000_000) return `${Math.round(n / 1000)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}
